/**
 * RESPONSIBILITY: one event, wherever it appears.
 *
 * There is ONE events list on this site. Home shows the soonest few, a ministry page
 * shows its own tag, the events page shows all of them — every one of those reads this
 * same row, so an event is entered once and cannot disagree with itself.
 *
 * A long row, deliberately: everything about an event belongs to the event. Each group is
 * headed by the plain question it answers, so "how do I hide this?" is found by heading
 * rather than by opening every panel. The card's four fields sit above, open; the rest
 * is closed until wanted, so adding an event is a title, a tag and a date.
 *
 * FOUR DIFFERENT DATE QUESTIONS, and no two of them are the same one:
 *
 *   startsAt / endsAt      when the event HAPPENS
 *   repeats                when it happens again, as a rule instead of a date
 *   showFrom / showUntil   when the page ADVERTISES it — a retreat in June may want
 *                          announcing in March and taking down the day after
 *   priorities             when it is IMPORTANT — see rows/priority.ts
 */
import type { ImageValue } from '@framework/core';
import {
  Row, Text, Paragraph, Img, Link, Bool, Num, Choice, DateField, Repeats, List,
} from '@framework/core/decorators';
import { PAGE_THEMES, PAGE_THEME } from '../themes';
import { PriorityRow } from './priority';
import { EventSectionRow } from './section';

const WHEN = 'When is it?';
const WHERE = 'Where is it?';
const SIGNUP = 'How do people sign up?';
const SEEN = 'Can visitors see it?';
const PLACED = 'Where on the site does it appear?';
const ORDER = 'Where does it sit in the lists?';
const PAGE = 'What is on its own page?';

@Row()
export class EventRow {
  @Text({ label: 'Title', maxLength: 300 })
  title!: string;

  /** Tags, not one category: VBS is Children AND Seasonal. Only tags on the Events list. */
  @Choice({
    label: 'Tags', in: 'categories', multiple: true,
    description: 'e.g. Children + Seasonal. An event may carry several.',
  })
  categories!: string[];

  @Bool({
    label: 'Show its tags', default: true,
    description: 'Untick and the tags below the card disappear. It stays filed under them.',
  })
  showTags!: boolean;

  @Paragraph({ label: 'Description', maxLength: 4000, rows: 3, markdown: true, allow: ['verse'] })
  summary!: string;

  @Img({
    label: 'Image', folder: 'events', maxSizeMb: 12, size: '1600 × 900',
    description: 'The picture on this event’s card.',
  })
  image!: ImageValue;

  @DateField({
    label: 'Starts', time: true, group: WHEN,
    description: 'For an event that happens once. Leave empty if it repeats.',
  })
  startsAt!: string;

  @DateField({ label: 'Ends', time: true, group: WHEN, description: 'When a one-off event finishes. Optional.' })
  endsAt!: string;

  /** A rule instead of a fixed date. When it next falls is computed, never typed. */
  @Repeats({
    label: 'Repeats', group: WHEN,
    description: 'For a weekly, monthly or yearly gathering. The card always shows the next date.',
  })
  repeats!: string;

  @Text({
    label: 'Date note', maxLength: 120, group: WHEN,
    description: 'Shown instead of a date when there is none. e.g. "Dates vary".',
  })
  dateNote!: string;

  @Text({
    label: 'Location', maxLength: 300, group: WHERE, collapsed: true,
    description: 'Where it happens. e.g. Main hall.',
  })
  location!: string;

  @Text({
    label: 'Button text', maxLength: 120, group: SIGNUP, collapsed: true,
    description: 'e.g. RSVP, Sign up, Register. Empty shows "Details".',
  })
  ctaLabel!: string;

  @Link({
    label: 'Button link', group: SIGNUP,
    description: 'Whatever form you already use — this site does not host registrations.',
  })
  ctaHref!: string;

  /** THE switch, and the only one. On by default — without one, new events were born hidden. */
  @Bool({
    label: 'Show this event', default: true, group: SEEN, collapsed: true,
    description: 'Untick to take it off the site entirely. You still see it, marked.',
  })
  showInMain!: boolean;

  @DateField({
    label: 'Show from', group: SEEN,
    description: 'Empty means from now. Before this date a visitor does not see it.',
  })
  showFrom!: string;

  @DateField({
    label: 'Show until', group: SEEN,
    description: 'Empty means for ever. After this date a visitor does not see it.',
  })
  showUntil!: string;

  @Bool({
    label: 'Featured', group: PLACED, collapsed: true,
    description: 'Also shown in the scrolling strip on the Events page and on Home.',
  })
  featured!: boolean;

  @Bool({
    label: 'Special', group: PLACED,
    description:
      'Shown in the Special Events band — a season or a gathering with its own site, '
      + 'rather than one entry in the dated list. Holy Week, VBS, ADC.',
  })
  special!: boolean;

  /** Hand ordering, for when soonest-first is not what the page should say. */
  @Num({
    label: 'Pin to the top', min: 0, max: 999, integer: true, default: 0,
    group: ORDER, collapsed: true,
    description:
      '0 leaves it in date order with everything else. 1 puts it first, 2 second, and so '
      + 'on — above everything still on 0.',
  })
  position!: number;

  /** `position`, asked of the calendar instead of a person. A window covering today wins. */
  @List({
    label: 'Move it up for a season', of: () => PriorityRow, maxItems: 12, group: ORDER,
    description:
      'One row per period. Each gives a rank and, if you want, the dates it applies '
      + 'between — the row covering today decides where this event sits in every list. '
      + 'Leave the dates empty and that row always applies.',
  })
  priorities!: PriorityRow[];

  /** Its own shape, so the page header is one size on every event. Empty fits the picture. */
  @Img({
    label: 'Banner', folder: 'events', maxSizeMb: 12, size: '1600 × 500',
    group: PAGE, collapsed: true,
    description: 'Across the top of this event’s page. Empty shows the picture above, fitted.',
  })
  bannerImage!: ImageValue;

  @Choice({
    label: 'Page shape', options: PAGE_THEMES, default: PAGE_THEME.Normal, group: PAGE,
    description:
      'Normal is one column, the whole width. "Register on right" keeps a narrow column '
      + 'down the right-hand side for a sign-up box or the details people need to hand.',
  })
  pageTheme!: string;

  @List({
    label: 'Bands down the page', of: () => EventSectionRow, maxItems: 24, group: PAGE,
    description:
      'Everything below the title, in the order it appears. Each band picks a theme, and '
      + 'the theme decides what you are then asked for — so start by choosing the theme.',
  })
  sections!: EventSectionRow[];

  /** The second stack. With no second column it follows the first — never hidden. */
  @List({
    label: 'Bands in the right column', of: () => EventSectionRow, maxItems: 8, group: PAGE,
    description:
      'Only used when the page shape has a right-hand column. Change the shape back and '
      + 'these follow the bands above instead — they are never hidden and never lost.',
  })
  railSections!: EventSectionRow[];

  /** Only when the title makes a poor link — the address already comes from the title. */
  @Text({
    label: 'Web address', maxLength: 300, group: PAGE,
    description: 'The last part of this event’s link. Leave empty to use the title.',
  })
  slug!: string;

  /** The address, made unique by the server from the preference in `slug`. */
  @Text({ label: 'Web address', derived: true })
  url!: string;
}
